import React, { useState, useEffect } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Mail, Users, Key, Eye, EyeOff } from 'lucide-react';
import { useTheme } from '@/components/theme-provider';
import { PhoneInput } from '@/components/ui/phone-input';
import { EmailInput } from '@/components/ui/email-input';

// Función para convertir texto a formato de nombre propio
const toProperCase = (str: string) => {
  return str
    .toLowerCase()
    .split(' ')
    .map(word => {
      // Preposiciones y artículos que deben ir en minúscula (excepto al inicio)
      const lowercaseWords = ['de', 'del', 'la', 'las', 'el', 'los', 'y', 'e', 'o', 'u', 'da', 'das', 'do', 'dos', 'van', 'von', 'der', 'le', 'mc', 'mac'];
      
      if (lowercaseWords.includes(word.toLowerCase())) {
        return word.toLowerCase();
      }
      
      // Casos especiales para nombres con Mc/Mac
      if (word.toLowerCase().startsWith('mc') || word.toLowerCase().startsWith('mac')) {
        return word.charAt(0).toUpperCase() + word.charAt(1).toLowerCase() + 
               word.charAt(2).toUpperCase() + word.slice(3).toLowerCase();
      }
      
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ')
    .trim()
    // Capitalizar primera palabra siempre
    .replace(/^./, str => str.toUpperCase());
};

// Función para validar contraseñas
const validatePassword = (password: string) => {
  if (password.length < 8) {
    return {
      isValid: false,
      message: 'La contraseña debe tener al menos 8 caracteres.'
    };
  }
  
  if (!/[A-Z]/.test(password)) {
    return {
      isValid: false,
      message: 'La contraseña debe incluir al menos una letra mayúscula.'
    };
  }
  
  if (!/[a-z]/.test(password)) {
    return {
      isValid: false,
      message: 'La contraseña debe incluir al menos una letra minúscula.'
    };
  }
  
  if (!/[0-9]/.test(password)) {
    return {
      isValid: false,
      message: 'La contraseña debe incluir al menos un número.'
    };
  }
  
  if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return {
      isValid: false,
      message: 'La contraseña debe incluir al menos un símbolo especial (!@#$%^&*(),.?":{}|<>).'
    };
  }
  
  // Lista de contraseñas comunes/débiles
  const weakPasswords = [
    'password', '12345678', 'qwerty123', 'abc123456', '123456789',
    'password123', 'admin123', 'user123', 'test123456', 'welcome123'
  ];
  
  if (weakPasswords.includes(password.toLowerCase())) {
    return {
      isValid: false,
      message: 'Esta contraseña es muy común. Elige una más segura.'
    };
  }
  
  return {
    isValid: true,
    message: 'Contraseña válida'
  };
};

// Función para generar contraseñas seguras
const generateSecurePassword = () => {
  const lowercase = 'abcdefghijklmnopqrstuvwxyz';
  const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const numbers = '0123456789';
  const symbols = '!@#$%^&*(),.?":{}|<>';
  
  // Asegurar que tenemos al menos uno de cada tipo
  let password = '';
  password += lowercase[Math.floor(Math.random() * lowercase.length)];
  password += uppercase[Math.floor(Math.random() * uppercase.length)];
  password += numbers[Math.floor(Math.random() * numbers.length)];
  password += symbols[Math.floor(Math.random() * symbols.length)];
  
  // Completar hasta 12 caracteres con caracteres aleatorios
  const allChars = lowercase + uppercase + numbers + symbols;
  for (let i = password.length; i < 12; i++) {
    password += allChars[Math.floor(Math.random() * allChars.length)];
  }
  
  // Mezclar los caracteres para que no tengan un patrón predecible
  return password.split('').sort(() => Math.random() - 0.5).join('');
};

export default function Auth() {
  const { user, loading, signIn, signUp } = useAuth();
  const [searchParams] = useSearchParams();
  const { theme } = useTheme();
  const currentTheme = (typeof window !== 'undefined' && window.matchMedia && theme === 'system')
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme;
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [brandingConfig, setBrandingConfig] = useState<{
    logo_url: string;
    system_name: string;
    tagline: string;
  }>({
    logo_url: "/lovable-uploads/logo.svg",
    system_name: "Modularis",
    tagline: "El ERP que piensa contigo"
  });
  const [registrationType, setRegistrationType] = useState<'public' | 'invitation' | 'company_code'>('company_code');
  const [invitationData, setInvitationData] = useState<any>(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [email, setEmail] = useState('');
  const [isRecoveryRedirecting, setIsRecoveryRedirecting] = useState(false);

  // Detectar inmediatamente el modo recovery desde la URL y forzar redirección preservando tokens
  useEffect(() => {
    const search = window.location.search;
    const hash = window.location.hash;
    const sp = new URLSearchParams(search);
    const hasRecoveryInUrl = sp.get('type') === 'recovery' || hash.includes('type=recovery');
    if (hasRecoveryInUrl) {
      try {
        localStorage.setItem('password_recovery_mode', 'true');
      } catch {}
      setIsRecoveryRedirecting(true);
      const target = `/reset-password${search}${hash}`;
      // Reemplazar para evitar bucles con el botón atrás
      window.location.replace(target);
    }
  }, []);
  // Load branding configuration
  useEffect(() => {
    loadBrandingConfig();
  }, []);

  // Check for invitation token in URL
  useEffect(() => {
    const token = searchParams.get('invitation_token');
    if (token) {
      setRegistrationType('invitation');
      loadInvitationData(token);
    }
  }, [searchParams]);

  const loadBrandingConfig = async () => {
    try {
      // Primero intentar cargar el branding global
      const { data: globalData, error: globalError } = await supabase
        .from('global_system_config')
        .select('config_value')
        .eq('config_key', 'default_company_branding')
        .maybeSingle();

      if (globalData?.config_value && typeof globalData.config_value === 'object') {
        const configValue = globalData.config_value as Record<string, any>;
        if (configValue.logo_url || configValue.system_name || configValue.tagline) {
          setBrandingConfig({
            logo_url: configValue.logo_url || "/lovable-uploads/logo.svg",
            system_name: configValue.system_name || "Modularis",
            tagline: configValue.tagline || "El ERP que piensa contigo"
          });
          return;
        }
      }

      // Si no hay configuración global, usar los valores por defecto
      console.log('Using default branding configuration');
    } catch (error) {
      console.error('Error loading branding config:', error);
      // Mantener valores por defecto en caso de error
    }
  };

  const loadInvitationData = async (token: string) => {
    try {
      const { data, error } = await supabase
        .from('user_invitations')
        .select(`
          *,
          companies(name),
          roles(name),
          branches(name)
        `)
        .eq('invitation_token', token)
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .single();

      if (error || !data) {
        setError('Invitación no válida o expirada');
        return;
      }

      setInvitationData(data);
    } catch (err) {
      setError('Error al cargar la invitación');
    }
  };

  // Bloquear mientras redirigimos al flujo de recovery
  if (isRecoveryRedirecting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect if already authenticated
  if (user && !loading) {
    // IMPORTANTE: No redirigir si estamos en modo password recovery
    const search = typeof window !== 'undefined' ? window.location.search : '';
    const hash = typeof window !== 'undefined' ? window.location.hash : '';
    const sp = new URLSearchParams(search);
    const hasRecoveryInUrl = sp.get('type') === 'recovery' || hash.includes('type=recovery');

    const isPasswordRecoveryMode = localStorage.getItem('password_recovery_mode') === 'true' || hasRecoveryInUrl;
    if (isPasswordRecoveryMode) {
      console.log('🔒 Usuario en modo recovery - no permitir acceso hasta cambiar contraseña');
      // Redirigir a la página de reset password si intentan acceder a /auth
      return <Navigate to={`/reset-password${search}${hash}`} replace />;
    }
    return <Navigate to="/" replace />;
  }

  const handleSignIn = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;

    const { error } = await signIn(email, password);
    
    if (error) {
      setError(error.message);
    }
    
    setIsLoading(false);
  };

  const handlePublicSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;
    const fullName = formData.get('fullName') as string;

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setIsLoading(false);
      return;
    }

    const { error } = await signUp(email, password, fullName);
    
    if (error) {
      setError(error.message);
    } else {
      setSuccess('Registro exitoso. Revisa tu email para confirmar tu cuenta.');
    }
    
    setIsLoading(false);
  };

  const handleInvitationSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData(e.currentTarget);
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setIsLoading(false);
      return;
    }

    try {
      // Sign up the user
      const { error: signUpError, data: authData } = await supabase.auth.signUp({
        email: invitationData.email,
        password: password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
        }
      });

      if (signUpError) {
        setError(signUpError.message);
        setIsLoading(false);
        return;
      }

      // Accept the invitation
      const { data: acceptResult, error: acceptError } = await supabase.rpc('accept_invitation', {
        invitation_token_param: invitationData.invitation_token
      });

      if (acceptError || (acceptResult as any)?.success === false) {
        setError((acceptResult as any)?.error || 'Error al aceptar la invitación');
        setIsLoading(false);
        return;
      }

      setSuccess('¡Registro completado! Ya puedes iniciar sesión.');
    } catch (err: any) {
      setError(err.message || 'Error durante el registro');
    }
    
    setIsLoading(false);
  };

  const handleCompanyCodeSignUp = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;
    const password = formData.get('password') as string;
    const confirmPassword = formData.get('confirmPassword') as string;
    const fullName = formData.get('fullName') as string;
    const companyCode = (formData.get('companyCode') as string).trim().toUpperCase();

    // Validar contraseña
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError(passwordValidation.message);
      setIsLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden');
      setIsLoading(false);
      return;
    }

    try {
      // Verify company code exists using security definer function
      console.log('🔍 Verificando código de empresa:', `"${companyCode}"`);
      console.log('🔍 Longitud del código:', companyCode.length);
      
      const { data: companies, error: companyError } = await supabase
        .rpc('validate_invitation_code', { code_to_check: companyCode });

      console.log('📊 Resultado de búsqueda:', { 
        companies, 
        companyError,
        searchedCode: companyCode 
      });

      if (companyError) {
        console.error('❌ Error en consulta de empresa:', companyError);
        setError('Error al verificar código de empresa. Intenta nuevamente.');
        setIsLoading(false);
        return;
      }

      if (!companies || companies.length === 0) {
        console.warn('⚠️ No se encontró empresa con código:', companyCode);
        setError('Código de empresa no encontrado. Verifica que sea correcto.');
        setIsLoading(false);
        return;
      }

      const company = companies[0];
      console.log('✅ Empresa encontrada:', company.name);

      // Sign up the user

      // Sign up the user
      const { error: signUpError } = await supabase.auth.signUp({
        email: email,
        password: password,
        options: {
          emailRedirectTo: `${window.location.origin}/`,
          data: {
            full_name: fullName,
            company_id: company.id,
            phone: phoneNumber
          }
        }
      });

      if (signUpError) {
        // Traducir errores comunes de Supabase al español
        let errorMessage = signUpError.message;
        
        if (errorMessage.includes('Password is known to be weak')) {
          errorMessage = 'La contraseña es muy débil. Debe tener al menos 8 caracteres, incluir mayúsculas, minúsculas, números y símbolos.';
        } else if (errorMessage.includes('Password should be at least')) {
          errorMessage = 'La contraseña debe tener al menos 6 caracteres.';
        } else if (errorMessage.includes('Invalid email')) {
          errorMessage = 'El formato del email no es válido.';
        } else if (errorMessage.includes('already registered')) {
          errorMessage = 'Este email ya está registrado. Intenta iniciar sesión.';
        } else if (errorMessage.includes('weak password')) {
          errorMessage = 'La contraseña es muy débil. Debe incluir mayúsculas, minúsculas, números y símbolos.';
        }
        
        setError(errorMessage);
        setIsLoading(false);
        return;
      }

      setSuccess(`¡Registro exitoso en ${company.name}! Revisa tu email para confirmar tu cuenta.`);
    } catch (err: any) {
      setError(err.message || 'Error durante el registro');
    }
    
    setIsLoading(false);
  };

  const handleForgotPassword = async () => {
    setShowForgotPassword(true);
    setError(null);
    setSuccess(null);
  };

  const handlePasswordReset = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    const formData = new FormData(e.currentTarget);
    const email = formData.get('email') as string;

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `https://pos.gruposyst.com/reset-password`,
    });

    if (error) {
      setError(error.message);
    } else {
      setSuccess('Se ha enviado un enlace de restablecimiento a tu email.');
    }

    setIsLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-3 sm:p-4 md:p-6">
      <div className="w-full max-w-sm sm:max-w-md">
        <div className="flex flex-col items-center justify-center mb-6 sm:mb-8 auth-header">
          <div className="p-8 rounded-2xl bg-card border border-border/20 shadow-sm hover:shadow-md transition-shadow duration-200">
            <img 
              src={brandingConfig.logo_url}
              alt={`${brandingConfig.system_name} Logo`}
              width="400"
              height="150"
              loading="eager"
              onError={(e) => {
                // Fallback a logo por defecto si la imagen falla
                e.currentTarget.src = '/lovable-uploads/logo.svg';
              }}
              className="h-16 sm:h-20 md:h-24 w-auto object-contain"
            />
          </div>
          <p className="text-sm font-medium text-muted-foreground text-center mt-4">
            {brandingConfig.tagline}
          </p>
          <Button
            variant="link"
            className="mt-2 text-xs text-primary hover:text-primary/90 underline-offset-4"
            onClick={() => window.open('/modularis', '_blank')}
          >
            Ver más sobre {brandingConfig.system_name} →
          </Button>
        </div>

        {showForgotPassword ? (
          <Card className="border border-border/20 rounded-2xl shadow-sm">
            <CardHeader className="space-y-3 pb-6">
              <h2 className="font-semibold tracking-tight text-xl">Restablecer Contraseña</h2>
              <CardDescription className="text-sm text-muted-foreground">
                Ingresa tu email para recibir un enlace de restablecimiento
              </CardDescription>
            </CardHeader>
            <form onSubmit={handlePasswordReset}>
              <CardContent className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="reset-email" className="text-sm font-medium">Email</Label>
                  <Input
                    id="reset-email"
                    name="email"
                    type="email"
                    placeholder="tu@email.com"
                    required
                    disabled={isLoading}
                    className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                  />
                </div>
              </CardContent>
              <CardFooter className="flex flex-col space-y-4 pt-2">
                  <Button 
                    type="submit" 
                    className="w-full h-12 text-base font-semibold rounded-xl bg-primary hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50" 
                    disabled={isLoading}
                  >
                  {isLoading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                  Enviar Enlace de Restablecimiento
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-12 text-base font-medium rounded-xl"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Volver al inicio de sesión
                </Button>
              </CardFooter>
            </form>
          </Card>
        ) : (
          <Tabs defaultValue="signin" className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-12 bg-muted p-1 rounded-xl border border-border/50">
              <TabsTrigger 
                value="signin" 
                className="text-sm font-medium rounded-lg data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm transition-all duration-200"
              >
                Iniciar Sesión
              </TabsTrigger>
              <TabsTrigger 
                value="signup" 
                className="text-sm font-medium rounded-lg data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm transition-all duration-200"
              >
                Registrarse
              </TabsTrigger>
            </TabsList>

          <TabsContent value="signin">
            <Card className="border border-border/20 rounded-2xl shadow-sm">
              <CardHeader className="space-y-3 pb-6">
                <h2 className="font-semibold tracking-tight text-xl">Iniciar Sesión</h2>
                <CardDescription className="text-sm text-muted-foreground">
                  Ingresa con tu cuenta para acceder al sistema
                </CardDescription>
              </CardHeader>
              <form onSubmit={handleSignIn}>
                <CardContent className="space-y-5">
                  <div className="space-y-2">
                    <Label htmlFor="signin-email" className="text-sm font-medium">Email</Label>
                    <Input
                      id="signin-email"
                      name="email"
                      type="email"
                      placeholder="tu@email.com"
                      required
                      disabled={isLoading}
                      className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="signin-password" className="text-sm font-medium">Contraseña</Label>
                    <Input
                      id="signin-password"
                      name="password"
                      type="password"
                      required
                      disabled={isLoading}
                      className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                    />
                  </div>
                  <div className="flex justify-end">
                    <Button
                      type="button"
                      variant="link"
                      className="p-0 h-auto text-sm text-primary hover:text-primary/80"
                      onClick={() => handleForgotPassword()}
                    >
                      ¿Olvidaste tu contraseña?
                    </Button>
                  </div>
                </CardContent>
                <CardFooter className="flex flex-col space-y-4 pt-2">
                  <Button 
                    type="submit" 
                    className="w-full h-12 text-base font-semibold rounded-xl bg-primary hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50" 
                    disabled={isLoading}
                  >
                    {isLoading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                    Iniciar Sesión
                  </Button>
                </CardFooter>
              </form>
            </Card>
          </TabsContent>

          <TabsContent value="signup">
            <Card className="border border-border/20 rounded-2xl shadow-sm">
              <CardHeader className="space-y-3 pb-6">
                <h2 className="font-semibold tracking-tight text-xl">Crear Cuenta</h2>
                <CardDescription className="text-sm text-muted-foreground">
                  {registrationType === 'invitation' && invitationData
                    ? `Invitación para ${invitationData.companies?.name}`
                    : registrationType === 'company_code'
                    ? 'Registro con código de empresa'
                    : 'Registra una nueva cuenta independiente'
                  }
                </CardDescription>
              </CardHeader>

              {/* Registration Type Selection - only show if not invitation */}
              {registrationType !== 'invitation' && (
                <CardContent className="pb-4">
                  <div className="grid grid-cols-1 gap-3">
                    <Button
                      type="button"
                      variant={registrationType === 'company_code' ? 'default' : 'outline'}
                      onClick={() => setRegistrationType('company_code')}
                      className="flex items-center justify-start gap-3 h-12 px-4"
                    >
                      <Key className="h-4 w-4" />
                      <div className="text-left">
                        <div className="font-medium">Código de Empresa</div>
                        <div className="text-xs text-muted-foreground">Tengo un código de empresa</div>
                      </div>
                    </Button>
                    <div className="text-xs text-muted-foreground text-center p-2">
                      Solo registro por código de empresa o invitación
                    </div>
                  </div>
                </CardContent>
              )}

              {/* Invitation Registration Form */}
              {registrationType === 'invitation' && invitationData && (
                <form onSubmit={handleInvitationSignUp}>
                  <CardContent className="space-y-5">
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Email (de la invitación)</Label>
                      <Input
                        value={invitationData.email}
                        disabled
                        className="h-12 text-base rounded-xl bg-muted/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Empresa</Label>
                      <Input
                        value={invitationData.companies?.name || 'N/A'}
                        disabled
                        className="h-12 text-base rounded-xl bg-muted/50"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Rol</Label>
                      <Input
                        value={invitationData.roles?.name || 'N/A'}
                        disabled
                        className="h-12 text-base rounded-xl bg-muted/50"
                      />
                    </div>
                    {invitationData.branches && (
                      <div className="space-y-2">
                        <Label className="text-sm font-medium">Sucursal</Label>
                        <Input
                          value={invitationData.branches.name}
                          disabled
                          className="h-12 text-base rounded-xl bg-muted/50"
                        />
                      </div>
                    )}
                    <div className="space-y-2">
                      <Label htmlFor="invitation-password" className="text-sm font-medium">Contraseña</Label>
                      <Input
                        id="invitation-password"
                        name="password"
                        type="password"
                        required
                        disabled={isLoading}
                        className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="invitation-confirm" className="text-sm font-medium">Confirmar Contraseña</Label>
                      <Input
                        id="invitation-confirm"
                        name="confirmPassword"
                        type="password"
                        required
                        disabled={isLoading}
                        className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                      />
                    </div>
                  </CardContent>
                  <CardFooter className="flex flex-col space-y-4 pt-2">
                    <Button 
                      type="submit" 
                      className="w-full h-12 text-base font-semibold rounded-xl bg-primary hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50" 
                      disabled={isLoading}
                    >
                      {isLoading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                      Aceptar Invitación y Registrarse
                    </Button>
                  </CardFooter>
                </form>
              )}

              {/* Public Registration Form */}
              {registrationType === 'public' && (
                <form onSubmit={handlePublicSignUp}>
                  <CardContent className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="public-fullname" className="text-sm font-medium">Nombre Completo</Label>
                      <Input
                        id="public-fullname"
                        name="fullName"
                        type="text"
                        placeholder="Tu nombre completo"
                        required
                        disabled={isLoading}
                        className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                      />
                      <div className="text-xs text-muted-foreground">
                        El nombre se formateará automáticamente (Ej: "juan pérez" → "Juan Pérez")
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="public-email" className="text-sm font-medium">Email</Label>
                      <Input
                        id="public-email"
                        name="email"
                        type="email"
                        placeholder="tu@email.com"
                        required
                        disabled={isLoading}
                        className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="public-password" className="text-sm font-medium">Contraseña</Label>
                      <Input
                        id="public-password"
                        name="password"
                        type="password"
                        required
                        disabled={isLoading}
                        className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="public-confirm" className="text-sm font-medium">Confirmar Contraseña</Label>
                      <Input
                        id="public-confirm"
                        name="confirmPassword"
                        type="password"
                        required
                        disabled={isLoading}
                        className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                      />
                    </div>
                  </CardContent>
                  <CardFooter className="flex flex-col space-y-4 pt-2">
                    <Button 
                      type="submit" 
                      className="w-full h-12 text-base font-semibold rounded-xl bg-primary hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50" 
                      disabled={isLoading}
                    >
                      {isLoading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                      Crear Cuenta Independiente
                    </Button>
                  </CardFooter>
                </form>
              )}

              {/* Company Code Registration Form */}
              {registrationType === 'company_code' && (
                <form onSubmit={handleCompanyCodeSignUp}>
                  <CardContent className="space-y-5">
                    <div className="space-y-2">
                      <Label htmlFor="code-company" className="text-sm font-medium">Código de Empresa</Label>
                      <Input
                        id="code-company"
                        name="companyCode"
                        type="text"
                        placeholder="DIGI-1234"
                        required
                        disabled={isLoading}
                        className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="code-fullname" className="text-sm font-medium">Nombre Completo</Label>
                      <Input
                        id="code-fullname"
                        name="fullName"
                        type="text"
                        placeholder="Tu nombre completo"
                        required
                        disabled={isLoading}
                        className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                        onBlur={(e) => {
                          // Formatear automáticamente a nombre propio cuando el usuario termine de escribir
                          const formattedName = toProperCase(e.target.value);
                          e.target.value = formattedName;
                        }}
                      />
                      <div className="text-xs text-muted-foreground">
                        El nombre se formateará automáticamente (Ej: "juan pérez" → "Juan Pérez")
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="code-email" className="text-sm font-medium">Email</Label>
                      <EmailInput
                        id="code-email"
                        name="email"
                        value={email}
                        onChange={setEmail}
                        placeholder="tu@email.com"
                        required
                        disabled={isLoading}
                        className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="code-phone" className="text-sm font-medium">Número de Teléfono</Label>
                      <PhoneInput
                        value={phoneNumber}
                        onChange={setPhoneNumber}
                        placeholder="Número de teléfono"
                        disabled={isLoading}
                      />
                    </div>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label htmlFor="code-password" className="text-sm font-medium">Contraseña</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const generatedPassword = generateSecurePassword();
                            const passwordInput = document.getElementById('code-password') as HTMLInputElement;
                            const confirmInput = document.getElementById('code-confirm') as HTMLInputElement;
                            if (passwordInput) passwordInput.value = generatedPassword;
                            if (confirmInput) confirmInput.value = generatedPassword;
                          }}
                          className="h-8 px-3 text-xs"
                          disabled={isLoading}
                        >
                          Generar
                        </Button>
                      </div>
                      <div className="relative">
                        <Input
                          id="code-password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          required
                          disabled={isLoading}
                          className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-12 px-3 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          disabled={isLoading}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>La contraseña debe incluir:</p>
                        <ul className="list-disc list-inside space-y-0.5 ml-2">
                          <li>Al menos 8 caracteres</li>
                          <li>Una letra mayúscula (A-Z)</li>
                          <li>Una letra minúscula (a-z)</li>
                          <li>Un número (0-9)</li>
                          <li>Un símbolo especial (!@#$%^&*)</li>
                        </ul>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="code-confirm" className="text-sm font-medium">Confirmar Contraseña</Label>
                      <div className="relative">
                        <Input
                          id="code-confirm"
                          name="confirmPassword"
                          type={showConfirmPassword ? "text" : "password"}
                          required
                          disabled={isLoading}
                          className="h-12 text-base rounded-xl border-muted-foreground/20 bg-background/50 focus:bg-background focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all duration-200 pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-12 px-3 hover:bg-transparent"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          disabled={isLoading}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                  <CardFooter className="flex flex-col space-y-4 pt-2">
                    <Button 
                      type="submit" 
                      className="w-full h-12 text-base font-semibold rounded-xl bg-primary hover:bg-primary/90 transition-colors duration-200 disabled:opacity-50" 
                      disabled={isLoading}
                    >
                      {isLoading && <Loader2 className="mr-2 h-5 w-5 animate-spin" />}
                      Registrarse con Código
                    </Button>
                  </CardFooter>
                </form>
              )}
            </Card>
          </TabsContent>
        </Tabs>
        )}

        {error && (
          <Alert className="mt-6 border-destructive/50 bg-destructive/5 text-destructive rounded-xl animate-fade-in">
            <AlertDescription className="text-sm font-medium">{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mt-6 border-primary/50 bg-primary/5 text-primary rounded-xl animate-fade-in">
            <AlertDescription className="text-sm font-medium">{success}</AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );
}